import { api } from "../utils/api";

const AssessmentProfileService = {
  add: async (profile, approval = true) => {
    profile.approved = approval;
    const response = await api.post("/api/assessment/profile", profile);
    return response.data;
  },

  getAll: async (user) => {
    try {
      const response = await api.get(
        `/api/assessment/profile/list?${user ? `user=${user}` : ""}`,
      );
      return response.data;
    } catch (error) {
      return [];
    }
  },
  get: async (profileId) => {
    try {
      const response = await api.get(`/api/assessment/profile/${profileId}`);
      return response.data;
    } catch (error) {
      return [];
    }
  },

  difficultyLevel: (level) => {
    let levelString = "Basic";
    if (level > 2) {
      levelString = "Intermediate";
    }
    if (level > 4) {
      levelString = "Advanced";
    }

    return levelString;
  },

  /**
   * Validates the profile object
   * @param {Object} profile - The profile object to validate
   * @returns {Object} - Object containing validation result and errors
   */
  validate: (profile) => {
    const errors = {
      basic: {},
      parameters: {},
      question_weight: {},
    };
    let isValid = true;

    // Validate basic information
    if (!profile.basic.name || profile.basic.name.trim() === "") {
      errors.basic.name = "Name is required";
      isValid = false;
    }

    if (!profile.basic.scope || profile.basic.scope.trim() === "") {
      errors.basic.scope = "Scope is required";
      isValid = false;
    }

    if (!profile.basic.description || profile.basic.description.trim() === "") {
      //errors.basic.description = "Description is required";
      //isValid = false;
    }

    // Validate parameters
    if (
      !profile.parameters.question_count ||
      profile.parameters.question_count <= 0
    ) {
      errors.parameters.question_count =
        "Question count must be greater than 0";
      isValid = false;
    } else if (profile.parameters.question_count > 100) {
      errors.parameters.question_count = "Question count cannot exceed 100";
      isValid = false;
    }

    if (!profile.parameters.duration || profile.parameters.duration <= 0) {
      errors.parameters.duration = "Duration must be greater than 0";
      isValid = false;
    } else if (profile.parameters.duration > 180) {
      errors.parameters.duration = "Duration cannot exceed 180 minutes";
      isValid = false;
    }

    if (
      profile.parameters.difficulty_level === undefined ||
      profile.parameters.difficulty_level < 1 ||
      profile.parameters.difficulty_level > 10
    ) {
      errors.parameters.difficulty_level =
        "Difficulty level must be between 1 and 10";
      isValid = false;
    }

    // Validate question weights
    const questionTypes = [
      "multiple_choice",
      "open_ended",
      "scenario_based",
      "technical_skills",
      "behavioral",
      "document_upload",
    ];

    let totalWeight = 0;

    for (const type of questionTypes) {
      if (
        profile.question_weight[type] === undefined ||
        profile.question_weight[type] < 0 ||
        !Number.isInteger(profile.question_weight[type])
      ) {
        errors.question_weight[type] =
          `${type.replace("_", " ")} weight must be a non-negative integer`;
        isValid = false;
      } else {
        totalWeight += profile.question_weight[type];
      }
    }

    // Check if at least one question type has a weight greater than 0
    if (totalWeight === 0) {
      errors.question_weight.general =
        "At least one question type must have a weight greater than 0";
      isValid = false;
    }

    return {
      isValid,
      errors,
    };
  },

  /**
   * Displays validation errors in an alert box with each error on a new line
   * @param {Object} validationResult - The result from validateProfile function
   */
  displayValidationErrors: (validationResult) => {
    if (validationResult.isValid) {
      return;
    }

    const { errors } = validationResult;
    let errorMessage = "Please fix the following errors:\n\n";

    // Basic errors
    if (errors.basic) {
      Object.entries(errors.basic).forEach(([field, message]) => {
        errorMessage += `${message}\n`;
      });
    }

    // Parameters errors
    if (errors.parameters) {
      Object.entries(errors.parameters).forEach(([field, message]) => {
        errorMessage += `${message}\n`;
      });
    }

    // Question weight errors
    if (errors.question_weight) {
      Object.entries(errors.question_weight).forEach(([field, message]) => {
        errorMessage += `${message}\n`;
      });
    }

    // Display the alert with all errors
    return errorMessage;
  },

  /* get all questions added to profile */
  getQuestions: async (profileId) => {
    try {
      const response = await api.get(
        "/api/assessment/profile/questions/" + profileId,
      );
      return response.data;
    } catch (error) {
      return { status: false, message: error.response.message };
    }
  },

  deleteProfile: async (profileId) => {
    try {
      const response = await api.delete("/api/assessment/profile/" + profileId);
      return response.data;
    } catch (error) {
      return { status: false, message: error.response.message };
    }
  },
};

export default AssessmentProfileService;
